import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ApiError, deleteTheme, fetchTheme, requestThemeImages } from '../api/client';
import type { Mode, ThemeView } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { creatorTokenFor, forgetCreatorToken } from '../auth/creatorToken';
import { AdSlot } from '../components/AdSlot';
import { ReportTheme } from '../components/ReportTheme';
import { Portrait } from '../components/Portrait';
import { ReuseAnswers } from '../components/ReuseAnswers';
import { ShareButtons } from '../components/ShareButtons';
import { VideoRequestForm } from '../components/VideoRequestForm';
import { perDayMax, useSite } from '../site/SiteProvider';
import { DEPTH_MODE_INFO, INSTRUMENT_MODE_INFO, recommendedMode } from '../diagnosis/labels';

/**
 * 生成した診断の入口（`/t/:themeId`）。SNS で共有されるのはこの URL。
 *
 * タイトルと紹介、キャラクターの顔ぶれ（4〜64 人）、深さの選択、シェアボタン。
 * 設問は全テーマ共通なので、診断そのものは `/shindan?theme=…` に任せる。
 * キャラの絵はテーマ作成後に裏で描かれる。`images.status` が pending / running のあいだは数秒おきに取り直し、
 * 描けた分から絵文字が絵に差し替わる。
 */
const IMAGE_POLL_MS = 5000;
/**
 * 混んでいると絵は 1 分に 1 枚ほどしか描けず、16 人で 15 分以上かかる（lib/images.ts の BusyPacer）。
 * はじめの 2 分は 5 秒おき、そのあとは 15 秒おきに取り直し、30 分で止める。
 */
const IMAGE_POLL_SLOW_MS = 15_000;
const IMAGE_POLL_SLOW_AFTER_MS = 2 * 60 * 1000;
const IMAGE_POLL_MAX_MS = 30 * 60 * 1000;
export function ThemeLandingPage() {
  const { themeId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const state = (location.state as { justCreated?: boolean; fallback?: boolean; typeCount?: number; requestedCount?: number; imageStatus?: string | null } | null) ?? null;

  const [theme, setTheme] = useState<ThemeView | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [working, setWorking] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [mode, setMode] = useState<Mode>(recommendedMode(undefined));

  const load = useCallback(async () => {
    const value = await fetchTheme(themeId);
    setTheme(value);
    return value;
  }, [themeId]);

  useEffect(() => {
    if (!themeId) return;
    let cancelled = false;
    setTheme(null);
    void fetchTheme(themeId)
      .then((value) => { if (!cancelled) { setTheme(value); setMode(recommendedMode(value.typeCount)); } })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof ApiError ? cause.message : 'この診断を開けませんでした。');
      });
    return () => { cancelled = true; };
  }, [themeId, auth.user]);

  // 結果ページの「相性ドラマの動画をリクエストする」から来たら、その欄まで送る
  useEffect(() => {
    if (theme && location.hash === '#video-request') document.getElementById('video-request')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [theme, location.hash]);

  /** 絵を描く（ログインせずに作った診断は、このブラウザの合言葉で引き取ってから描く）。 */
  const onDrawImages = useCallback(async () => {
    setWorking(true);
    setError('');
    try {
      const response = await requestThemeImages(themeId, { creatorToken: creatorTokenFor(themeId) });
      if (response.claimed) forgetCreatorToken(themeId);
      setNotice(response.started ? 'キャラクターの絵を描きはじめました。できた順に差し替わります。' : response.enabled ? 'いまの絵の状態は変わりません。' : 'この環境では絵を描きません。');
      await Promise.all([load(), auth.refresh()]);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '絵を描きはじめられませんでした。');
    } finally {
      setWorking(false);
    }
  }, [auth, load, themeId]);

  const onDelete = useCallback(async () => {
    if (!theme) return;
    const warn = (theme.plays ?? 0) > 0 ? `${theme.plays} 人が受けた診断です。` : '';
    if (!window.confirm(`「${theme.title}」を消します。${warn}この入口は開けなくなります（すでに出ている結果は残ります）。よろしいですか？`)) return;
    setWorking(true);
    try {
      await deleteTheme(themeId);
      setDeleted(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'この診断を消せませんでした。');
    } finally {
      setWorking(false);
    }
  }, [theme, themeId]);

  // 絵ができるまでポーリング（描けた分から差し替わる）。だんだん間を空け、30 分で止める
  const imagesStatus = theme?.images?.status ?? null;
  useEffect(() => {
    if (!themeId || (imagesStatus !== 'pending' && imagesStatus !== 'running')) return;
    const startedAt = Date.now();
    let cancelled = false;
    let timer = 0;
    const next = () => {
      const elapsed = Date.now() - startedAt;
      if (cancelled || elapsed > IMAGE_POLL_MAX_MS) return;
      timer = window.setTimeout(() => {
        void fetchTheme(themeId).then((value) => { if (!cancelled) setTheme(value); }).catch(() => undefined).finally(next);
      }, elapsed < IMAGE_POLL_SLOW_AFTER_MS ? IMAGE_POLL_MS : IMAGE_POLL_SLOW_MS);
    };
    next();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [themeId, imagesStatus]);

  if (deleted) {
    return (
      <main className="page">
        <h1 className="page__title">この診断を消しました</h1>
        <p className="page__lead">
          一覧と入口から消えました。すでに出ている結果ページはそのまま開けます。戻したいときは管理者に頼んでください。
        </p>
        <Link className="button button--primary" to="/">トップへ戻る</Link>
      </main>
    );
  }
  if (error && !theme) {
    return (
      <main className="page">
        <h1 className="page__title">この診断は開けません</h1>
        <p className="page__lead">{error}</p>
        <Link className="button button--primary" to="/">トップへ戻る</Link>
      </main>
    );
  }
  if (!theme) return <p className="page__notice">診断を開いています…</p>;

  const url = `${window.location.origin}/t/${theme.themeId}`;
  const shareText = `「${theme.title}」あなたはどの${theme.subject}？ #GenDan`;

  return (
    <main className="page theme">
      {state?.justCreated && (
        <div className="theme__created" role="status">
          <p className="theme__created-title">診断ができました。</p>
          <p className="theme__created-body">
            {state.fallback
              ? 'いま生成が混み合っていたため、用意してあった「日本むかし話」の顔ぶれで作りました。'
              : `Gemini が ${theme.characterCount} 人のキャラクターを考えました。`}
            {state.requestedCount && state.typeCount && state.requestedCount !== state.typeCount
              ? `${state.requestedCount} 人には分けられないので ${state.typeCount} 人にしました。`
              : ''}
            下のボタンから友達に送れます。まず自分で受けてみるのもおすすめです。
          </p>
        </div>
      )}

      {theme.origin === 'generated' && <p className="page__kicker">みんなが作った診断</p>}
      <h1 className="page__title theme__title">{theme.title}</h1>
      <p className="page__lead">{theme.intro}</p>

      <div className="theme__actions">
        <button className="button button--cta" type="button" onClick={() => navigate(`/shindan?theme=${encodeURIComponent(theme.themeId)}&mode=${mode}`)}>
          診断をはじめる
        </button>
        <ShareButtons url={url} text={shareText} title={theme.title} compact />
      </div>

      <ReuseAnswers mode={mode} themeId={theme.themeId} />

      <fieldset className="modes">
        <legend className="modes__legend">どこまで答えますか</legend>
        {DEPTH_MODE_INFO.map((info) => (
          <label key={info.mode} className={`mode${mode === info.mode ? ' mode--selected' : ''}`}>
            <input className="mode__radio" type="radio" name="mode" value={info.mode} checked={mode === info.mode} onChange={() => setMode(info.mode)} />
            <span className="mode__name">{info.label}{info.mode === recommendedMode(theme.typeCount) ? <span className="mode__badge">この診断におすすめ</span> : null}</span>
            <span className="mode__meta">{info.count}問 ／ {info.minutes}</span>
            <span className="mode__summary">{info.summary}</span>
          </label>
        ))}
      </fieldset>

      {/* 別の質問紙（性格 5 因子・60 問）。深さではなく測り方が違う（doc/ASSESSMENT.md §9） */}
      <fieldset className="modes modes--instrument">
        <legend className="modes__legend">別の測り方で</legend>
        {INSTRUMENT_MODE_INFO.map((info) => (
          <label key={info.mode} className={`mode${mode === info.mode ? ' mode--selected' : ''}`}>
            <input className="mode__radio" type="radio" name="mode" value={info.mode} checked={mode === info.mode} onChange={() => setMode(info.mode)} />
            <span className="mode__name">{info.label}</span>
            <span className="mode__meta">{info.count}問 ／ {info.minutes}</span>
            <span className="mode__summary">{info.summary} <Link to="/help/bigfive">ビッグファイブとは</Link></span>
          </label>
        ))}
      </fieldset>

      <section className="section">
        <h2 className="section__title">登場する {theme.characterCount} 人</h2>
        {(imagesStatus === 'pending' || imagesStatus === 'running') && (
          <p className="section__note" role="status" aria-live="polite">
            キャラクターの絵を描いています（{theme.images?.done ?? 0} / {theme.images?.total ?? theme.characterCount}）。できた順に差し替わります。
          </p>
        )}
        <ImageInvite theme={theme} working={working} onDraw={() => void onDrawImages()} />
        {notice && <p className="section__note" role="status">{notice}</p>}
        {error && theme && <p className="page__error" role="alert">{error}</p>}
        <ul className="chars">
          {theme.characters.map((c) => (
            <li key={c.id} className="char">
              <Portrait brief={{ name: c.name, portrait: c.portrait, emoji: c.emoji }} size={160} className="char__face" loading="lazy" />
              <div className="char__text">
                <p className="char__label">{c.label}</p>
                <p className="char__name">{c.name}</p>
                <p className="char__epithet">{c.epithet}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <VideoRequestForm
        themeId={theme.themeId}
        themeTitle={theme.title}
        characters={theme.characters}
        initialId={new URLSearchParams(location.search).get('video')}
      />

      {theme.prompt && (
        <section className="section">
          <h2 className="section__title">この診断を生んだ一文</h2>
          <blockquote className="theme__prompt">{theme.prompt}</blockquote>
          <p className="section__note">
            あなたも <Link to="/">トップの「自分で作る」</Link> から、別のテーマで作れます。
          </p>
        </section>
      )}

      {(theme.viewer?.canDelete || theme.ownerless) && (
        <section className="section theme__manage">
          <h2 className="section__title">この診断を管理する</h2>
          <p className="section__note">
            {theme.viewer?.isOwner
              ? 'あなたが作った診断です。'
              : theme.viewer?.isAdmin && !theme.ownerless
                ? '管理者として操作できます。'
                : 'ログインせずに作られた診断なので、誰でも消せます。'}
            消すと一覧と入口から消えます。すでに出ている結果ページは残ります。
          </p>
          <button className="button button--danger" type="button" onClick={() => void onDelete()} disabled={working}>
            この診断を消す
          </button>
        </section>
      )}

      <AdSlot placement="landing" allowed={theme.adsAllowed} />

      <p className="page__footnote">
        ログインしなくても最後まで受けられます。結果は断定ではなく「傾向」として出します。
      </p>
      <ReportTheme themeId={theme.themeId} />
    </main>
  );
}

/**
 * 絵が無い診断への案内（doc/AUTH.md §5）。
 * ログインせずに作った診断は、作ったブラウザに残る合言葉があれば、ログインして引き取り絵を付けられる。
 */
function ImageInvite({ theme, working, onDraw }: { readonly theme: ThemeView; readonly working: boolean; readonly onDraw: () => void }) {
  const { ready, user, enabled, busy, requestSignIn } = useAuth();
  const { limits } = useSite();
  const status = theme.images?.status ?? null;
  if (!ready || !enabled || theme.origin !== 'generated') return null;
  // 絵が無い・足りない診断だけ。images が null なのは、ログインの仕組みより前に作られた古い診断
  if (status !== null && status !== 'login_required' && status !== 'quota' && status !== 'partial') return null;

  const hasToken = Boolean(creatorTokenFor(theme.themeId));
  // 描けるのは作った本人（と合言葉を持つ引き取り人）だけ。管理者が他の人の診断に描くのは管理メニューの「診断」から
  const mine = theme.viewer?.isOwner ?? false;

  if (!user) {
    return (
      <p className="section__note theme__invite">
        {status === 'quota'
          ? 'この診断を作った日は絵の上限に達していたので、まだ絵がありません。'
          : status === 'login_required'
            ? 'この診断はログインせずに作られたので、絵はありません。'
            : 'この診断にはまだ絵がありません。'}
        {hasToken && `ログインすると、この診断を自分のものにして絵を付けられます（${perDayMax(limits.userImageDailyLimit)}）。`}
        {hasToken && (
          <button className="button button--ghost button--small maker__note-button" type="button" onClick={() => void requestSignIn()} disabled={busy}>
            ログインして絵を付ける
          </button>
        )}
      </p>
    );
  }
  if (!mine && !hasToken) return null;

  return (
    <p className="section__note theme__invite">
      {status === 'partial' ? '絵が一部だけできています。' : 'この診断にはまだ絵がありません。'}
      <button className="button button--ghost button--small maker__note-button" type="button" onClick={onDraw} disabled={working}>
        {working ? '始めています…' : 'キャラクターの絵を描く'}
      </button>
    </p>
  );
}
